import { type ReactNode, useEffect, useRef } from 'react';
import {
  CalendarClock,
  ContactRound,
  CornerDownLeft,
  PanelsTopLeft,
  Plus,
  ReceiptText,
  UsersRound,
} from 'lucide-react';
import { useTranslation } from '@doccareplus/shared/lib';
import { cn } from '@doccareplus/shared/lib/utils';
import { SearchInput, Skeleton, SkeletonCircle } from '@doccareplus/ui';
import type { SearchCategory } from '../../domain/searchResult';
import type { GlobalSearchController } from './useGlobalSearchController';

/** Fallback icon per category for entity rows that don't carry their own icon. */
function categoryIcon(category: SearchCategory): ReactNode {
  switch (category) {
    case 'patients':
      return <ContactRound className="h-[17px] w-[17px]" aria-hidden="true" />;
    case 'appointments':
      return <CalendarClock className="h-[17px] w-[17px]" aria-hidden="true" />;
    case 'invoices':
      return <ReceiptText className="h-[17px] w-[17px]" aria-hidden="true" />;
    case 'staff':
      return <UsersRound className="h-[17px] w-[17px]" aria-hidden="true" />;
    case 'actions':
      return <Plus className="h-[17px] w-[17px]" aria-hidden="true" />;
    case 'pages':
    default:
      return <PanelsTopLeft className="h-[17px] w-[17px]" aria-hidden="true" />;
  }
}

export interface GlobalSearchPanelProps {
  controller: GlobalSearchController;
  autoFocus?: boolean;
  /** Show the ↑↓/↵/esc footer legend (desktop palette only). */
  showKeyboardHints?: boolean;
}

/**
 * GlobalSearchPanel — the shared body of the global search (input + grouped, keyboard-navigable
 * results). Rendered inside the desktop command palette and the mobile search sheet alike, so the
 * two surfaces differ only in their chrome (overlay vs full-screen sheet), never in behaviour.
 */
export function GlobalSearchPanel({ controller, autoFocus = true, showKeyboardHints = false }: GlobalSearchPanelProps) {
  const { t } = useTranslation('common');
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the keyboard-highlighted row visible as the user arrows through results.
  useEffect(() => {
    if (!controller.activeId || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(controller.activeId)}"]`);
    if (typeof el?.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [controller.activeId]);

  return (
    <div className="flex flex-col min-h-0">
      <div className="p-2.5">
        <SearchInput
          value={controller.term}
          onChange={(event) => controller.setTerm(event.target.value)}
          onClear={() => controller.setTerm('')}
          onKeyDown={controller.handleKeyDown}
          clearLabel={t('actions.clear')}
          placeholder={t('search.placeholder')}
          aria-label={t('search.placeholder')}
          autoFocus={autoFocus}
        />
      </div>

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2.5" role="listbox" aria-label={t('search.title')}>
        {controller.isFetching && controller.flat.length === 0 && (
          <div role="status" aria-label={t('search.title')} className="space-y-1.5 px-1 py-2">
            <span className="sr-only">{t('search.title')}</span>
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 rounded-[10px] px-2 py-2" aria-hidden>
                <SkeletonCircle size={32} className="rounded-[9px]" />
                <span className="flex-1">
                  <Skeleton className="h-2.5 w-2/5" />
                  <Skeleton className="mt-1.5 h-2 w-3/5" />
                </span>
              </div>
            ))}
          </div>
        )}

        {controller.showEmpty && (
          <div className="px-3 py-10 text-center">
            <div className="text-[13.5px] font-semibold text-[var(--text-2)]">
              {t('search.empty', { term: controller.term.trim() })}
            </div>
            <div className="mt-1 text-[12px] text-[var(--text-3)]">{t('search.emptyHint')}</div>
          </div>
        )}

        {controller.groups.map((group) => (
          <div key={group.category} className="mb-1.5 last:mb-0">
            <div className="px-2 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--text-3)]">
              {group.category === 'pages' && !controller.hasQuery
                ? t('search.suggestions')
                : t(`search.categories.${group.category}`)}
            </div>
            {group.items.map((item) => {
              const active = item.id === controller.activeId;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-item-id={item.id}
                  onMouseMove={() => controller.setActiveId(item.id)}
                  onClick={item.onSelect}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-[10px] px-2 py-2 text-left transition-colors cursor-pointer',
                    active ? 'bg-[var(--surface-2)]' : 'hover:bg-[var(--surface-2)]',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 flex-none items-center justify-center rounded-[9px]',
                      active ? 'bg-[var(--primary-weak)] text-[var(--primary)]' : 'bg-[var(--surface-2)] text-[var(--text-2)]',
                    )}
                  >
                    {item.icon ?? categoryIcon(item.category)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-[var(--text)]">{item.title}</span>
                    {item.subtitle && (
                      <span className="mt-0.5 block truncate text-[12px] text-[var(--text-3)]">{item.subtitle}</span>
                    )}
                  </span>
                  {item.meta && (
                    <span className="flex-none rounded-full bg-[var(--surface-3)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--text-2)]">
                      {item.meta}
                    </span>
                  )}
                  {active && (
                    <CornerDownLeft className="h-3.5 w-3.5 flex-none text-[var(--text-3)]" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {showKeyboardHints && (
        <div className="flex items-center gap-4 border-t border-[var(--border)] px-4 py-2 text-[11px] text-[var(--text-3)]">
          <span className="flex items-center gap-1.5">
            <kbd className="kbd">↑</kbd>
            <kbd className="kbd">↓</kbd>
            {t('search.hintNavigate')}
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="kbd">↵</kbd>
            {t('search.hintSelect')}
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="kbd">esc</kbd>
            {t('search.hintClose')}
          </span>
        </div>
      )}
    </div>
  );
}
